/**
 * 跑马灯工具类
 * 支持水平滚动和垂直滚动，可以传入显示文字而不影响容器原有内容
 */
class Marquee {
  constructor(container, text, options = {}) {
    this.container =
      typeof container === "string"
        ? document.querySelector(container)
        : container;

    if (!this.container) {
      throw new Error("容器元素不存在");
    }

    // 保存原始内容
    this.originalContent = this.container.innerHTML;
    this.text = text || "";

    // 默认配置
    this.config = {
      mode: "marquee", // 显示模式: 'marquee' | 'fixed'
      direction: "horizontal", // 滚动方向: 'horizontal' | 'vertical'
      speed: 50, // 滚动速度 (px/s)
      pauseOnHover: false, // 鼠标悬停时是否暂停
      loop: true, // 是否循环滚动
      gap: 0, // 内容之间的间距
      randomStart: true, // 是否随机起始位置（仅跑马灯模式）
      density: "medium", // 固定展示密度: 'low' | 'medium' | 'high'
      opacity: 1, // 文案透明度: 0-1
      zIndex: 10,
      color: "#fff", // 文案颜色: CSS颜色值
      // 显示范围设置
      top: 0, // 顶部边距 (px)
      left: 0, // 左侧边距 (px)
      right: 0, // 左侧边距 (px)
      bottom: 0, // 底部边距 (px)
      fontSize: "14px",
      loopCount: 0, // 播放次数: 0无限次
      interval: 0, // 轮播间隔时间 (ms)
      ...options,
    };

    this.currentLoopCount = this.config.loopCount;

    this.isRunning = false;
    this.animationId = null;
    this.startTime = 0;
    this.pausedTime = 0;
    this.totalPausedTime = 0;

    // 容器变化监听相关
    this.resizeObserver = null;
    this.mutationObserver = null;
    this.containerResizeHandler = null;

    this.init();
  }

  /**
   * 初始化跑马灯
   */
  init() {
    this.setupContainer();
    this.setupContent();
    this.bindEvents();
    this.setupObservers();
  }

  /**
   * 设置容器样式
   */
  setupContainer() {
    const { direction } = this.config;

    this.container.style.overflow = "hidden";
    this.container.style.position = "relative";

    if (direction === "horizontal") {
      this.container.style.whiteSpace = "nowrap";
    } else {
      this.container.style.height = this.container.offsetHeight + "px";
    }
  }

  /**
   * 设置内容样式和克隆
   */
  setupContent() {
    const { mode, direction, density } = this.config;

    // 先清理现有元素
    if (this.marqueeWrapper && this.marqueeWrapper.parentNode) {
      this.container.removeChild(this.marqueeWrapper);
      this.marqueeWrapper = null;
    }
    if (this.fixedWrapper && this.fixedWrapper.parentNode) {
      this.container.removeChild(this.fixedWrapper);
      this.fixedWrapper = null;
    }

    if (mode === "marquee") {
      this.setupMarqueeMode();
    } else if (mode === "fixed") {
      this.setupFixedMode();
    }
  }

  /**
   * 设置跑马灯模式
   */
  setupMarqueeMode() {
    const { direction, opacity, color, top, left, right, bottom, zIndex } =
      this.config;

    // 先删除之前的跑马灯包装器
    if (this.marqueeWrapper && this.marqueeWrapper.parentNode) {
      this.marqueeWrapper.parentNode.removeChild(this.marqueeWrapper);
    }

    // 创建跑马灯内容包装器
    this.marqueeWrapper = document.createElement("div");
    this.marqueeWrapper.style.position = "absolute";
    this.marqueeWrapper.style.top = top + "px";
    this.marqueeWrapper.style.left = left + "px";
    // 不设置right和bottom，避免inset: 0的效果
    if (right > 0) {
      this.marqueeWrapper.style.right = right + "px";
    }
    if (bottom > 0) {
      this.marqueeWrapper.style.bottom = bottom + "px";
    }
    this.marqueeWrapper.style.zIndex = zIndex;
    this.marqueeWrapper.style.pointerEvents = "none";
    this.marqueeWrapper.style.opacity = opacity;

    if (direction === "horizontal") {
      this.marqueeWrapper.style.display = "inline-block";
      this.marqueeWrapper.style.whiteSpace = "nowrap";
    }

    // 创建跑马灯文本元素
    this.marqueeText = document.createElement("span");
    this.marqueeText.innerHTML = this.text;
    this.marqueeText.style.display = "inline-block";
    this.marqueeText.style.whiteSpace = "nowrap";
    this.marqueeText.style.pointerEvents = "none";
    this.marqueeText.style.fontSize = this.config.fontSize;
    this.marqueeText.style.color = color;
    this.marqueeText.style.userSelect = "none";
    this.marqueeText.style.textStroke = "0.3px rgba(0,0,0,0.2)";
    this.marqueeText.style["-webkit-text-stroke"] = "0.3px rgba(0,0,0,0.2)";
    this.marqueeWrapper.appendChild(this.marqueeText);
    this.container.appendChild(this.marqueeWrapper);
  }

  /**
   * 设置固定展示模式
   */
  setupFixedMode() {
    const { density, opacity, color, top, left, right, bottom, zIndex } =
      this.config;

    // 先删除之前的固定展示包装器
    if (this.fixedWrapper && this.fixedWrapper.parentNode) {
      this.fixedWrapper.parentNode.removeChild(this.fixedWrapper);
    }

    // 创建固定展示包装器
    this.fixedWrapper = document.createElement("div");
    this.fixedWrapper.style.position = "absolute";
    this.fixedWrapper.style.top = top + "px";
    this.fixedWrapper.style.left = left + "px";
    this.fixedWrapper.style.right = right + "px";
    this.fixedWrapper.style.bottom = bottom + "px";
    this.fixedWrapper.style.zIndex = zIndex;
    this.fixedWrapper.style.pointerEvents = "none";
    this.fixedWrapper.style.overflow = "hidden";
    this.fixedWrapper.style.opacity = opacity;

    // 根据密度设置字体大小和间距
    const densityConfig = this.getDensityConfig(density);

    // 计算有效显示区域的尺寸
    const containerWidth = this.container.offsetWidth;
    const containerHeight = this.container.offsetHeight;
    const effectiveWidth = containerWidth - left - right;
    const effectiveHeight = containerHeight - top - bottom;

    // 创建文案网格
    this.createTextGrid(effectiveWidth, effectiveHeight, densityConfig, color);
    this.container.appendChild(this.fixedWrapper);
  }

  /**
   * 获取密度配置
   */
  getDensityConfig(density) {
    const configs = {
      low: {
        fontSize: "16px",
        lineHeight: "24px",
        letterSpacing: "1px",
        rowSpacing: 400, // 行间距较大
        colSpacing: 400, // 列间距较大
        rowOffset: 200, // 行错开偏移
      },
      medium: {
        fontSize: "16px",
        lineHeight: "24px",
        letterSpacing: "1px",
        rowSpacing: 350, // 行间距中等
        colSpacing: 350, // 列间距中等
        rowOffset: 200, // 行错开偏移
      },
      high: {
        fontSize: "16px",
        lineHeight: "24px",
        letterSpacing: "1px",
        rowSpacing: 300, // 行间距较小
        colSpacing: 300, // 列间距较小
        rowOffset: 200, // 行错开偏移
      },
    };
    return configs[density] || configs.medium;
  }

  /**
   * 创建文案网格（从中间往四周排列）
   */
  createTextGrid(containerWidth, containerHeight, densityConfig, color) {
    const {
      fontSize,
      lineHeight,
      letterSpacing,
      rowSpacing,
      colSpacing,
      rowOffset,
    } = densityConfig;

    // 安全检查
    if (!this.text || containerWidth <= 0 || containerHeight <= 0) {
      return;
    }

    // 计算文案宽度
    const textWidth = this.estimateTextWidth(
      this.text,
      fontSize,
      letterSpacing
    );

    // 安全检查：避免除零或创建过多元素
    if (textWidth <= 0 || rowSpacing <= 0 || colSpacing <= 0) {
      return;
    }

    // 计算中心点
    const centerX = containerWidth / 2;
    const centerY = containerHeight / 2;

    // 使用螺旋算法从中心向外排列
    const positions = this.generateSpiralPositions(
      centerX,
      centerY,
      textWidth,
      parseFloat(lineHeight),
      colSpacing,
      rowSpacing,
      rowOffset,
      containerWidth,
      containerHeight
    );

    // 创建文案元素
    positions.forEach(({ x, y }) => {
      const textElement = document.createElement("span");
      textElement.textContent = this.text;
      textElement.style.position = "absolute";
      textElement.style.fontSize = fontSize;
      textElement.style.lineHeight = lineHeight;
      textElement.style.letterSpacing = letterSpacing;
      textElement.style.whiteSpace = "nowrap";
      textElement.style.pointerEvents = "none";
      textElement.style.color = color;
      textElement.style.left = x + "px";
      textElement.style.top = y + "px";
      this.fixedWrapper.appendChild(textElement);
    });
  }

  /**
   * 生成以中心为基准的错开布局位置
   */
  generateSpiralPositions(
    centerX,
    centerY,
    textWidth,
    textHeight,
    colSpacing,
    rowSpacing,
    rowOffset,
    containerWidth,
    containerHeight
  ) {
    const positions = [];
    const visited = new Set();

    // 1. 首先在容器中心放置一个元素
    const centerXPos = centerX - textWidth / 2;
    const centerYPos = centerY - textHeight / 2;
    const centerRowPosi = [];
    if (
      centerXPos >= 0 &&
      centerXPos + textWidth <= containerWidth &&
      centerYPos >= 0 &&
      centerYPos + textHeight <= containerHeight
    ) {
      positions.push({ x: centerXPos, y: centerYPos });
      visited.add(`${Math.round(centerXPos)},${Math.round(centerYPos)}`);
    }
    // 3. 在当前行向左右扩展（各插入10个水印）
    for (let i = 1; i <= 10; i++) {
      // 向左插入
      const leftX = centerXPos - (textWidth + colSpacing) * i;
      const leftY = centerYPos;

      const leftkey = `${Math.round(leftX)},${Math.round(leftY)}`;
      if (!visited.has(leftkey)) {
        positions.push({ x: leftX, y: leftY });
        visited.add(leftkey);
      }
      // 向右插入
      const rightX = centerXPos + (textWidth + colSpacing) * i;
      const rightY = centerYPos;

      const rightKey = `${Math.round(rightX)},${Math.round(rightY)}`;
      if (!visited.has(rightKey)) {
        positions.push({ x: rightX, y: rightY });
        visited.add(rightKey);
      }
    }
    centerRowPosi.push(...positions);
    centerRowPosi.forEach((item) => {
      const baseX = item.x;
      const baseY = item.y;
      for (let i = 1; i <= 10; i++) {
        const upX = baseX + (i % 2 ? rowOffset : 0);
        const upY = baseY - (rowSpacing + textHeight) * i;
        const upkey = `${Math.round(upX)},${Math.round(upY)}`;
        const downX = baseX + rowOffset;
        const downY = baseY + (rowSpacing + textHeight) * i;
        const downkey = `${Math.round(downX)},${Math.round(downY)}`;
        if (!visited.has(upkey)) {
          positions.push({
            x: upX,
            y: upY,
          });
          visited.add(upkey);
        }
        if (!visited.has(downkey)) {
          positions.push({
            x: downX,
            y: downY,
          });
          visited.add(downkey);
        }
      }
    });

    return positions;
  }
  /**
   * 估算文字宽度
   */
  estimateTextWidth(text, fontSize, letterSpacing) {
    // 创建临时元素来测量文字宽度
    const tempElement = document.createElement("span");
    tempElement.style.position = "absolute";
    tempElement.style.visibility = "hidden";
    tempElement.style.fontSize = fontSize;
    tempElement.style.letterSpacing = letterSpacing;
    tempElement.style.whiteSpace = "nowrap";
    tempElement.textContent = text;

    document.body.appendChild(tempElement);
    const width = tempElement.offsetWidth;
    document.body.removeChild(tempElement);

    return width;
  }

  /**
   * 绑定事件
   */
  bindEvents() {
    if (this.config.pauseOnHover) {
      this.container.addEventListener(
        "mouseenter",
        this.handleMouseEnter.bind(this)
      );
      this.container.addEventListener(
        "mouseleave",
        this.handleMouseLeave.bind(this)
      );
    }
  }

  /**
   * 设置容器变化监听器
   */
  setupObservers() {
    // 监听容器尺寸变化
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (entry.target === this.container) {
            console.log(333333);

            this.handleContainerResize();
          }
        }
      });
      this.resizeObserver.observe(this.container);
    } else {
      // 降级方案：使用window resize事件
      this.containerResizeHandler = this.debounce(() => {
        this.handleContainerResize();
      }, 100);
      window.addEventListener("resize", this.containerResizeHandler);
    }
  }

  /**
   * 防抖函数
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * 处理容器尺寸变化
   */
  handleContainerResize() {
    // 重新设置容器样式
    this.setupContainer();

    // 临时禁用 MutationObserver 避免死循环
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }

    // 重新设置内容（特别是固定展示模式需要重新生成布局）
    this.setupContent();

    // 重新启用 MutationObserver
    if (this.mutationObserver) {
      this.mutationObserver.observe(this.container, {
        childList: true,
        subtree: false, // 只监听直接子元素变化
      });
    }

    // 如果跑马灯正在运行，重新计算位置
    if (this.isRunning) {
      this.startTime = performance.now() - this.totalPausedTime;
    }
  }

  /**
   * 处理容器内容变化
   */
  handleContainerChange() {
    console.log(111111111);

    // 保存新的原始内容
    this.originalContent = this.container.innerHTML;

    // 重新设置跑马灯内容
    this.setupContent();

    // 如果跑马灯正在运行，重新开始
    if (this.isRunning) {
      this.startTime = performance.now() - this.totalPausedTime;
    }
  }

  /**
   * 鼠标进入事件
   */
  handleMouseEnter() {
    this.pause();
  }

  /**
   * 鼠标离开事件
   */
  handleMouseLeave() {
    this.resume();
  }

  /**
   * 开始滚动
   */
  start() {
    console.log("this.isRunning", this.isRunning);
    console.log("this.config.mode", this.config.mode);

    if (this.isRunning) return;

    // 固定展示模式不需要滚动
    if (this.config.mode === "fixed") return;

    this.isRunning = true;
    this.startTime = performance.now() - this.totalPausedTime;

    // 如果启用随机起始位置，设置随机偏移
    if (this.config.randomStart) {
      this.setRandomStartPosition();
    } else {
      // 如果不启用随机起始位置，设置为0
      this.randomStartOffsetX = 0;
      this.randomStartOffsetY = 0;
    }

    this.animate();
  }

  /**
   * 设置随机起始位置
   */
  setRandomStartPosition() {
    const { direction, top, left, right, bottom } = this.config;

    if (direction === "horizontal") {
      const containerWidth = this.container.offsetWidth;
      const containerHeight = this.container.offsetHeight;
      const textWidth = this.marqueeText.offsetWidth;
      const textHeight = this.marqueeText.offsetHeight;

      // 计算有效显示区域
      const effectiveWidth = containerWidth - left - right;
      const effectiveHeight = containerHeight - top - bottom;

      // X位置：始终从容器最右边开始（不随机）
      const randomXOffset = 0;
      // 随机Y位置：0到有效高度-文字高度之间
      const randomYOffset =
        Math.random() * Math.max(0, effectiveHeight - textHeight);

      this.randomStartOffsetX = randomXOffset;
      this.randomStartOffsetY = randomYOffset;
    } else {
      const containerWidth = this.container.offsetWidth;
      const containerHeight = this.container.offsetHeight;
      const textWidth = this.marqueeText.offsetWidth;
      const textHeight = this.marqueeText.offsetHeight;

      // 计算有效显示区域
      const effectiveWidth = containerWidth - left - right;
      const effectiveHeight = containerHeight - top - bottom;

      // Y位置：始终从容器最下边开始（不随机）
      const randomYOffset = 0;
      // 随机X位置：0到有效宽度-文字宽度之间
      const randomXOffset =
        Math.random() * Math.max(0, effectiveWidth - textWidth);

      this.randomStartOffsetX = randomXOffset;
      this.randomStartOffsetY = randomYOffset;
    }
  }

  /**
   * 暂停滚动
   */
  pause() {
    if (!this.isRunning) return;

    this.isRunning = false;
    this.pausedTime = performance.now();
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
  }

  /**
   * 恢复滚动
   */
  resume() {
    if (this.isRunning) return;

    this.totalPausedTime += performance.now() - this.pausedTime;
    this.start();
  }

  /**
   * 停止滚动
   */
  stop() {
    this.isRunning = false;
    this.totalPausedTime = 0;
    this.pausedTime = 0;

    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }

    // 重置位置（安全检查）
    if (this.marqueeWrapper && this.marqueeWrapper.parentNode) {
      this.marqueeWrapper.style.transform = "translate(0, 0)";
    }
  }

  /**
   * 动画循环
   */
  animate() {
    if (!this.isRunning) return;

    const currentTime = performance.now();
    const elapsed =
      (currentTime - this.startTime - this.totalPausedTime) / 1000;
    const { direction, speed, loop } = this.config;

    let translateX = 0;
    let translateY = 0;

    if (direction === "horizontal") {
      const textWidth = this.marqueeText.offsetWidth;
      const containerWidth = this.container.offsetWidth;
      const { left, right } = this.config;

      // 计算有效显示区域宽度
      const effectiveWidth = containerWidth - left - right;

      // 从右侧开始滚动，加上随机X偏移
      const startPositionX = effectiveWidth + (this.randomStartOffsetX || 0);
      translateX = startPositionX - elapsed * speed;

      // 设置随机Y位置
      translateY = this.randomStartOffsetY || 0;

      if (loop) {
        // 当文字完全移出左侧边界时，重置到右侧（重新随机）
        if (translateX <= -textWidth) {
          if (this.currentLoopCount > 0) {
            this.currentLoopCount--;
            if (this.currentLoopCount === 0) {
              this.stop();
              return;
            }
          }
          if (this.config.interval > 0) {
            this.isRunning = false;
            if (this.marqueeWrapper) this.marqueeWrapper.style.display = 'none';
            setTimeout(() => {
              this.startTime = performance.now() - this.totalPausedTime;
              if (this.config.randomStart) {
                this.setRandomStartPosition();
              }
              if (this.marqueeWrapper) this.marqueeWrapper.style.display = 'inline-block';
              this.isRunning = true;
              this.animate();
            }, this.config.interval);
            return;
          }
          this.startTime = currentTime - this.totalPausedTime;
          if (this.config.randomStart) {
            this.setRandomStartPosition();
          }
          translateX = effectiveWidth + (this.randomStartOffsetX || 0);
          translateY = this.randomStartOffsetY || 0;
        }
      } else {
        // 非循环模式：当文字完全移出左侧时停止
        if (translateX <= -textWidth) {
          this.stop();
          return;
        }
      }
    } else {
      const textHeight = this.marqueeText.offsetHeight;
      const containerHeight = this.container.offsetHeight;
      const { top, bottom } = this.config;

      // 计算有效显示区域高度
      const effectiveHeight = containerHeight - top - bottom;

      // 从下方开始滚动，加上随机Y偏移
      const startPositionY = effectiveHeight + (this.randomStartOffsetY || 0);
      translateY = startPositionY - elapsed * speed;

      // 设置随机X位置
      translateX = this.randomStartOffsetX || 0;

      if (loop) {
        // 当文字完全移出上边界时，重置到下侧（重新随机）
        if (translateY <= -textHeight) {
          if (this.currentLoopCount > 0) {
            this.currentLoopCount--;
            if (this.currentLoopCount === 0) {
              this.stop();
              return;
            }
          }
          if (this.config.interval > 0) {
            this.isRunning = false;
            if (this.marqueeWrapper) this.marqueeWrapper.style.display = 'none';
            setTimeout(() => {
              this.startTime = performance.now() - this.totalPausedTime;
              if (this.config.randomStart) {
                this.setRandomStartPosition();
              }
              if (this.marqueeWrapper) this.marqueeWrapper.style.display = 'block';
              this.isRunning = true;
              this.animate();
            }, this.config.interval);
            return;
          }
          this.startTime = currentTime - this.totalPausedTime;
          if (this.config.randomStart) {
            this.setRandomStartPosition();
          }
          translateY = effectiveHeight + (this.randomStartOffsetY || 0);
          translateX = this.randomStartOffsetX || 0;
        }
      } else {
        // 非循环模式：当文字完全移出上侧时停止
        if (translateY <= -textHeight) {
          this.stop();
          return;
        }
      }
    }

    // 应用变换
    if (this.marqueeWrapper) {
      this.marqueeWrapper.style.transform = `translate(${translateX}px, ${translateY}px)`;
    }

    // 不再需要处理克隆元素，使用单个文案来回滚动

    this.animationId = requestAnimationFrame(() => this.animate());
  }

  /**
   * 销毁跑马灯
   */
  destroy() {
    this.stop();

    // 移除事件监听
    if (this.config.pauseOnHover) {
      this.container.removeEventListener("mouseenter", this.handleMouseEnter);
      this.container.removeEventListener("mouseleave", this.handleMouseLeave);
    }

    // 清理观察器
    this.cleanupObservers();

    // 移除跑马灯元素，恢复原始内容
    if (this.marqueeWrapper && this.marqueeWrapper.parentNode) {
      this.container.removeChild(this.marqueeWrapper);
      this.marqueeWrapper = null;
    }
    if (this.fixedWrapper && this.fixedWrapper.parentNode) {
      this.container.removeChild(this.fixedWrapper);
      this.fixedWrapper = null;
    }
  }

  /**
   * 清理观察器
   */
  cleanupObservers() {
    // 清理ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    // 清理MutationObserver
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }

    // 清理window resize事件监听
    if (this.containerResizeHandler) {
      window.removeEventListener("resize", this.containerResizeHandler);
      this.containerResizeHandler = null;
    }
  }

  /**
   * 更新文本内容
   */
  updateText(newText) {
    this.text = newText || "";
    if (this.marqueeText) {
      this.marqueeText.textContent = this.text;
    }
    // 固定模式下需要重新创建网格
    if (this.config.mode === "fixed" && this.fixedWrapper) {
      this.container.removeChild(this.fixedWrapper);
      this.setupFixedMode();
    }
  }

  /**
   * 更新配置
   */
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
    this.currentLoopCount = this.config.loopCount || 0;

    // 先清理现有元素
    if (this.marqueeWrapper && this.marqueeWrapper.parentNode) {
      this.container.removeChild(this.marqueeWrapper);
      this.marqueeWrapper = null;
    }
    if (this.fixedWrapper && this.fixedWrapper.parentNode) {
      this.container.removeChild(this.fixedWrapper);
      this.fixedWrapper = null;
    }

    // 然后停止动画（避免操作已删除的元素）
    this.stop();

    // 重新设置容器样式和内容
    this.setupContainer();
    this.setupContent();
  }
}

/**
 * 创建跑马灯的便捷方法
 * @param {string|HTMLElement} container - 容器元素或选择器
 * @param {string} text - 跑马灯显示的文字
 * @param {Object} options - 配置选项
 * @returns {Marquee} 跑马灯实例
 */
export function createMarquee(container, text, options = {}) {
  return new Marquee(container, text, options);
}

/**
 * 默认配置的跑马灯方法
 * @param {string|HTMLElement} container - 容器元素或选择器
 * @param {string} text - 跑马灯显示的文字
 * @param {Object} options - 配置选项
 * @returns {Marquee} 跑马灯实例
 */
export function marquee(container, text, options = {}) {
  const instance = createMarquee(container, text, options);
  instance.start();
  return instance;
}

export default Marquee;
